import DateFnsUtils from "@date-io/date-fns";
import { Button, Typography, makeStyles } from "@material-ui/core";
import { DatePicker as MuiDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { de, enGB, fr, es, it, nl, pl, pt, ro } from "date-fns/locale";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { IconCalendar } from "src/common/icons";
import { displayDate } from "src/helpers";

const localeMap = {
  de,
  en: enGB,
  fr,
  es,
  it,
  nl,
  pl,
  pt,
  ro
};

const useStyles = makeStyles((theme: any) => ({
  button: {
    background: "#ffffff",
    border: "1px solid #bdbdbd",
    color: "#595959",
    display: "flex" as "flex",
    height: 45,
    justifyContent: "flex-start" as "flex-start",
    lineHeight: "4.5rem",
    minWidth: 145,
    padding: "0 15px",
    textTransform: "none" as "none",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      height: 36,
      lineHeight: "3.6rem",
      width: "auto" as "auto"
    }
  },
  dateText: {
    fontSize: "1.4rem"
  },
  hidden: {
    display: "none" as "none"
  },
  icon: {
    fontSize: "2.4rem",
    margin: "0 14px 0 0",
    order: 1,
    position: "relative" as "relative",
    top: "-2px",
    [theme.breakpoints.up("md")]: {
      fontSize: "2.2rem",
      margin: "0 0 0 15px",
      order: 2
    }
  },
  mobileLabel: {
    display: "block" as "block",
    fontSize: "1rem",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  textContainer: {
    display: "inline-block",
    lineHeight: 1,
    order: 2,
    textAlign: "left" as "left",
    [theme.breakpoints.up("md")]: {
      order: 1
    }
  }
}));

interface IOwnProps {
  btnWrapperClass?: string | undefined;
  onChange: (arg0: Date) => void;
  defaultValue?: Date | null;
  disablePast?: boolean;
  disableWeekends?: boolean;
  disabled?: boolean;
  minDate?: Date;
  label?: string;
  dataTestId?: string;
}

const checkWeekend = (date: Date) => {
  // 0 - Sunday, 6 - Saturday
  return [0, 6].includes(date.getDay());
};

const DatePicker = ({
  btnWrapperClass,
  onChange,
  defaultValue = null,
  disablePast = false,
  disabled = false,
  disableWeekends = false,
  minDate,
  label,
  dataTestId
}: IOwnProps) => {
  const classes = useStyles();

  const {
    i18n: { language },
    t
  } = useTranslation();

  const [date, changeDate] = React.useState(defaultValue ? defaultValue : null);
  const [pickerOpen, setPickerOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (defaultValue !== date) {
      changeDate(defaultValue);
    }
  }, [defaultValue]);

  const handleChange = (newDate: Date) => {
    changeDate(newDate);
    if (onChange) {
      onChange(newDate);
    }
  };

  const onOpenCalendarButtonClick = React.useCallback(() => !disabled && setPickerOpen(true), []);

  const onDatePickerClose = React.useCallback(() => setPickerOpen(false), []);

  return (
    <MuiPickersUtilsProvider locale={localeMap[language]} utils={DateFnsUtils}>
      <div className={btnWrapperClass}>
        <Button
          disabled={disabled}
          className={classes.button}
          onClick={onOpenCalendarButtonClick}
          data-qa-selector={`${dataTestId}-btn`}
        >
          <div className={classes.textContainer} data-qa-selector="datePickerTextContainer">
            {label ? <span className={classes.mobileLabel}>{label}</span> : null}
            <span className={classes.dateText}>{date ? displayDate(date) : t("feedback.datePicker.selectDate")}</span>
          </div>
          <Typography
            className={classes.icon}
            color="primary"
            display="inline"
            data-qa-selector="datePickerCalendarIcon"
          >
            <IconCalendar />
          </Typography>
        </Button>
      </div>
      <div className={classes.hidden}>
        <MuiDatePicker
          autoOk
          okLabel={t("feedback.dialog.googleCalendarLink.button.ok")}
          cancelLabel={t("feedback.commonComponents.buttons.cancel")}
          data-qa-selector={dataTestId}
          disablePast={disablePast}
          minDate={minDate}
          onChange={handleChange}
          onClose={onDatePickerClose}
          open={pickerOpen}
          shouldDisableDate={disableWeekends ? checkWeekend : undefined}
          value={date}
        />
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default React.memo(DatePicker);
